import { AlertTriangle } from "lucide-react";
import * as React from "react";
import { Link, useParams } from "react-router-dom";

import { useRun } from "@/api/runs";
import { PageHeader } from "@/components/layout/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { DEFAULT_SERIES_KIND, overlaySearchParams } from "@/lib/torsion-overlay";

/**
 * The SAME lazy boundary `RunDetail` puts on the same component
 * (flowstack#992) — same `import()` specifier, so both mount points share
 * one chunk rather than duplicating recharts. Declared here rather than
 * imported from the run page because that is this codebase's idiom for a
 * heavy widget with two mount points (`DAGCanvas`, in `SubmitFlow` and
 * `RunWorkspace`).
 *
 * It has to be here too: this page renders `AnalysisTab` unconditionally,
 * so a static import would keep recharts statically reachable from a route
 * and put it straight back in the entry chunk — the boundary on the run
 * page alone would buy nothing.
 */
const AnalysisTab = React.lazy(() =>
  import("@/components/runs/AnalysisTab").then((m) => ({ default: m.AnalysisTab }))
);

interface RouteParams {
  slug?: string;
  run_id?: string;
  [key: string]: string | undefined;
}

/**
 * Analysis surface, step 3 — a single run's analysis (flowstack VIZ U4.1).
 *
 * Deliberately a thin wrapper, not a second rendering path: it renders the
 * SAME `AnalysisTab` the per-run detail page's Analysis tab uses (#922),
 * unchanged, and unconditionally — nothing in this file ever withholds it.
 * Every empty/error state D71(iii) names for a specific run —
 * not-generated-yet, and the 403/404/5xx/network split (flowstack#923) —
 * comes along for free because this is the identical component, not a
 * reimplementation of it. This unit's only job is getting the user here
 * with a project + run picked, not computing or rendering analysis.
 */
export function AnalysisRunViewPage(): JSX.Element {
  const { slug = "", run_id = "" } = useParams<RouteParams>();
  const run = useRun(run_id);

  // Rupert PR-935/936 r0: `slug` in the URL is not authoritative — it is
  // not checked against the run's actual project, so an edited or stale
  // URL could show a breadcrumb claiming project X while the analysis
  // shown belongs to a run from project Y. Deliberately a NON-BLOCKING
  // banner, not a hard refusal that replaces AnalysisTab: `project_slug`
  // is `PILOT_PROJECT_SLUG if record.project_id == PILOT_PROJECT_ID else
  // None` (src/flowstack/server/routes/runs.py) — a pilot-era constant
  // comparison, not a real per-run project lookup. It happens to agree
  // with the projects endpoint's own slug today (both derive from the
  // same PILOT_PROJECT_SLUG constant — src/flowstack/server/projects.py),
  // but nothing enforces that once real multi-project support lands, and
  // this heuristic signal has no server-side authority. A false positive
  // from a hard gate would make an otherwise-reachable analysis
  // UNREACHABLE from this surface — a strictly worse failure than the
  // cosmetic breadcrumb inaccuracy the check exists to catch. A banner
  // degrades instead of blocking, and — since AnalysisTab now renders
  // unconditionally regardless of this check's timing — there is no
  // longer a race between "the report shows" and "the check resolves"
  // for a test to need to cover.
  const projectMismatch = Boolean(run.data?.project_slug && slug && run.data.project_slug !== slug);

  const breadcrumbs = (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Link to="/analysis" className="hover:text-foreground">
        Analysis
      </Link>
      <span aria-hidden>·</span>
      <Link to={`/analysis/${slug}`} className="font-mono hover:text-foreground">
        {slug}
      </Link>
      <span aria-hidden>·</span>
      <span className="font-mono">{run_id}</span>
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      {breadcrumbs}
      <PageHeader
        title="Analysis"
        description={run_id}
        actions={
          <div className="flex items-center gap-4">
            {/* VIZ U2.1: carry this run into the comparison already selected,
                so "now compare it with another" is one click and the link the
                user lands on is already the shareable one.

                Built through `overlaySearchParams`, never as a template
                string (Rupert PR-993 r0): the point of putting the encoding in
                one module is that no call site can drift from it, and a
                hand-written `?runs=…&series=qm` here would hard-code both the
                parameter names and the default kind. */}
            <Link
              to={`/analysis/${slug}/compare?${overlaySearchParams({
                runIds: [run_id],
                torsionIds: [],
                series: DEFAULT_SERIES_KIND,
              })}`}
              className="text-sm font-medium text-cyan hover:underline"
            >
              Compare with another run →
            </Link>
            <Link
              to={`/runs/${run_id}`}
              className="text-sm font-medium text-muted-foreground hover:text-foreground hover:underline"
            >
              View full run →
            </Link>
          </div>
        }
      />
      {projectMismatch && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-md border-l-4 border-state-failed-validation-fg bg-state-failed-validation-fg/5 px-4 py-3 text-xs text-foreground"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 text-state-failed-validation-fg" aria-hidden />
          <span>
            This run&apos;s reported project does not match{" "}
            <span className="font-mono">{slug}</span>.{" "}
            <Link to={`/runs/${run_id}`} className="font-medium underline">
              View it on its run page →
            </Link>
          </span>
        </div>
      )}
      <React.Suspense fallback={<Skeleton className="h-64 w-full" />}>
        <AnalysisTab runId={run_id} />
      </React.Suspense>
    </div>
  );
}
